/**
 * Archies — optimistic Add to Cart.
 *
 * Removes the perceived wait on add-to-cart: the cart drawer opens INSTANTLY rendered as the FULL
 * loaded cart (header + free-shipping bar + line + summary + Secure Checkout), instead of after the
 * /cart/add.js round-trip. The server add is unchanged and stays authoritative — this is a purely
 * visual pre-render that the real cart reconciles over via morphSection.
 *
 * It mirrors the real cart's markup/classes (cart-products-ext, free-shipping-component,
 * cart-summary-ext) so (a) it looks identical to the loaded cart and (b) the morph on cart:update is
 * minimal/seamless rather than a wholesale replace.
 *
 * Flow (rides the existing event plumbing, zero core JS edits):
 *   tap  → snapshot the drawer markup → render the optimistic cart → open + bump count → announce.
 *   ok   → product-form fires `cart:update`; CartItemsComponent morphSections the REAL cart in. We
 *          just clear pending state.
 *   fail → product-form fires `cart:error`; we restore the snapshot, revert the count, close the
 *          drawer, and announce the error.
 *
 * Display names follow the theme convention everywhere: bold line = `custom.product_color` (US
 * spelling; legacy `custom.product_colour` is the migration fallback), secondary =
 * `custom.product_title` (never the raw Shopify product.title — that's the last-resort fallback).
 * Exposed on add-to-cart-component as data-product-color / data-product-title.
 *
 * Scope: PDP/standard product add (the size-matched child SKU is already in the form's hidden `id`).
 * Free-gift auto-add is intentionally NOT optimistic. Collection quick-add is out of scope.
 */
(function () {
  'use strict';

  var pending = false;
  var snapshot = null; // { html, bubbleText }
  var liveRegion = null;

  // English fallbacks; the real strings come from cart-drawer-component[data-i18n] (locales/*.json).
  var I18N_DEFAULTS = {
    adding: 'Adding…',
    added: 'Added to cart',
    error: 'Sorry — that couldn’t be added to your cart.',
    cartTitle: 'Your Cart (%count%)',
    cartTitleLabel: 'Your Cart',
    itemCount: '1 item',
    subtotal: 'Subtotal',
    estimatedShipping: 'Estimated Shipping',
    calculatedAtCheckout: 'Calculated at checkout',
    secureCheckout: 'Secure Checkout',
    free: 'FREE',
    close: 'Close',
    cart: 'Cart'
  };

  function i18n() {
    var drawer = getDrawer();
    var t = {};
    if (drawer) { try { t = JSON.parse(drawer.getAttribute('data-i18n') || '{}'); } catch (e) { t = {}; } }
    for (var k in I18N_DEFAULTS) { if (!t[k]) t[k] = I18N_DEFAULTS[k]; }
    return t;
  }

  // Quantity-selector icons (match assets/icon-minus.svg / icon-plus.svg).
  var ICON_MINUS = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>';
  var ICON_PLUS = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>';

  // Lock icon (matches snippets/icons-ext.liquid `lock`) for the optimistic Secure Checkout button.
  var LOCK_SVG ='<svg class="checkout-locked" fill="currentColor" height="30px" width="30px" viewBox="0 0 330 330" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M65,330h200c8.284,0,15-6.716,15-15V145c0-8.284-6.716-15-15-15h-15V85c0-46.869-38.131-85-85-85S80,38.131,80,85v45H65c-8.284,0-15,6.716-15,15v170C50,323.284,56.716,330,65,330z M180,234.986V255c0,8.284-6.716,15-15,15s-15-6.716-15-15v-20.014c-6.068-4.565-10-11.824-10-19.986c0-13.785,11.215-25,25-25s25,11.215,25,25C190,223.162,186.068,230.421,180,234.986z M110,85c0-30.327,24.673-55,55-55s55,24.673,55,55v45H110V85z"/></svg>';
  // Mirrors Horizon's icon-delete.svg (line trash). Same --icon-stroke-width as the server cart's trash.
  var ICON_DELETE = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function esc(s) {
    return (s || '').replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function announce(message, assertive) {
    /**
     * [Archies 2026-09-30] WCAG 4.1.3. This wrote to a region appended to <body>, and the success
     * path announces AFTER drawer.open() — which is showModal(). That makes everything outside the
     * dialog inert, and an inert subtree is removed from the accessibility tree, so "Added to cart"
     * was never spoken. (The error path already worked by accident: it calls drawer.close() first,
     * so body is not inert by the time it announces.)
     *
     * #cart-live-region lives inside the dialog shell for exactly this reason. Prefer it whenever
     * the drawer is open; the body node is still correct when it is closed, because then the
     * in-dialog region is the one that is hidden.
     */
    var dlg = document.querySelector('dialog.cart-drawer__dialog');
    if (dlg && dlg.open) {
      var inDialog = document.getElementById('cart-live-region');
      if (inDialog) {
        inDialog.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
        inDialog.textContent = '';
        setTimeout(function () { inDialog.textContent = message; }, 50);
        return;
      }
    }

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }
    liveRegion.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = message; }, 50);
  }

  // A4 (WCAG 4.1.3 Status Messages): announce the resulting cart state on every cart:update — this is
  // what makes server-path quantity changes and removals audible to screen readers (previously silent;
  // the count node is aria-hidden and the status total is display:none). Reads the ALREADY-TRANSLATED
  // heading ("Your Cart (N)") + subtotal from the morphed DOM, so no English/hardcoded copy is added.
  function announceCartUpdate() {
    var region = document.getElementById('cart-live-region');
    if (!region) return;
    var heading = document.querySelector('.cart-drawer__heading');
    var subtotal =
      document.querySelector('.cart-summary-totals-v2 .subtotal-items-price') ||
      document.querySelector('.subtotal-items-price');
    // [Archies 2026-09-30] Join across element boundaries with a space. textContent runs adjacent
    // spans together, and this heading is a label span plus a count span — it was announcing
    // "Your Cart3 items".
    var flatten = function (el) {
      if (!el) return '';
      var bits = [];
      el.querySelectorAll('*').length
        ? el.childNodes.forEach(function (n) {
            var t = (n.textContent || '').replace(/\s+/g, ' ').trim();
            if (t) bits.push(t);
          })
        : bits.push((el.textContent || '').replace(/\s+/g, ' ').trim());
      return bits.join(' ').replace(/\s+/g, ' ').trim();
    };
    var parts = [];
    if (heading) parts.push(flatten(heading));
    if (subtotal) parts.push(flatten(subtotal));
    var msg = parts.join('. ');
    if (!msg) return;
    region.textContent = '';
    setTimeout(function () { region.textContent = msg; }, 60);
  }

  function getDrawer() {
    return document.querySelector('cart-drawer-component');
  }

  function cartItems() {
    return (
      document.querySelector('cart-drawer-component cart-items-component') ||
      document.querySelector('cart-items-component')
    );
  }

  function cartBubble() {
    return (
      document.querySelector('.header-actions__cart-icon .cart-bubble__text') ||
      document.querySelector('.header-actions__cart-icon .cart-bubble')
    );
  }

  // Compact Shopify money formatter — mirrors the `money` filter using shop.money_format.
  function formatMoney(cents, format) {
    cents = Math.round(cents || 0);
    format = format || '${{amount}}';
    function withDelim(sep, decSep, decimals) {
      if (decimals == null) decimals = 2;
      var amount = (cents / 100).toFixed(decimals);
      var parts = amount.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      // Drop the decimal entirely when there are no cents (mirrors `money_without_trailing_zeros`):
      // $20.00 → $20, but $45.95 stays $45.95.
      var hasCents = decimals && parts[1] && parseInt(parts[1], 10) !== 0;
      return hasCents ? intPart + decSep + parts[1] : intPart;
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
      switch (key) {
        case 'amount_no_decimals': return withDelim(',', '.', 0);
        case 'amount_with_comma_separator': return withDelim('.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return withDelim('.', ',', 0);
        case 'amount_with_apostrophe_separator': return withDelim("'", '.', 2);
        default: return withDelim(',', '.', 2); // amount
      }
    });
  }

  function getConfig(drawer) {
    var cfg = null, fmt = '${{amount}}';
    if (drawer) {
      fmt = drawer.getAttribute('data-money-format') || fmt;
      try { cfg = JSON.parse(drawer.getAttribute('data-free-shipping') || 'null'); } catch (e) { cfg = null; }
    }
    return { fs: cfg, money: fmt, t: i18n() };
  }

  // Pull the display data for the line being added. Display-only; the server resolves the actual
  // variant from the form's hidden `id`.
  function readLine(button, money) {
    var atc = button.closest('add-to-cart-component');
    var img = (atc && atc.getAttribute('data-product-variant-media')) || '';
    var colour = (atc && atc.getAttribute('data-product-color')) || '';
    var title = (atc && atc.getAttribute('data-product-title')) || '';
    var subcategory = (atc && atc.getAttribute('data-product-subcategory')) || '';
    if (!title) {
      var titleEl = document.querySelector('.product-information h1, .product-title, [data-product-title] h1, [data-product-title]');
      title = titleEl ? titleEl.textContent.trim() : (document.title || '').split('–')[0].trim();
    }

    var variant = '';
    var priceCents = NaN;
    var checked = document.querySelector('.variant-tab-wrapper-size .group-block-paret-container:not([hidden]) input:checked')
      || document.querySelector('variant-picker fieldset input:checked');
    if (checked) {
      // Full variant name (e.g. "US Men's 5 / US Women's 6") to match the server-rendered cart line —
      // not the short visual label ("US 6"). The radio carries it on value / aria-label.
      variant = checked.getAttribute('value') || checked.getAttribute('aria-label') || '';
      if (!variant) {
        var label = checked.closest('label');
        var textEl = label && (label.querySelector('.variant-label-text-x') || label);
        variant = textEl ? textEl.textContent.replace(/\s+/g, ' ').trim() : '';
      }
      priceCents = parseInt(checked.getAttribute('data-variant-price-amount') || '', 10);
    }
    if (isNaN(priceCents)) priceCents = parseInt((atc && atc.getAttribute('data-product-price-cents')) || '', 10);
    if (isNaN(priceCents)) {
      var priceEl = document.querySelector('.add-to-cart-price, .product-information .price, [data-product-price]');
      var raw = priceEl ? priceEl.textContent.replace(/[^\d.]/g, '') : '';
      priceCents = raw ? Math.round(parseFloat(raw) * 100) : 0;
    }
    return {
      img: img,
      colour: colour,
      title: title,
      subcategory: subcategory,
      variant: variant,
      priceCents: priceCents || 0,
      price: formatMoney(priceCents || 0, money),
      qty: 1
    };
  }

  // `units` is the eligible item count, used only when cfg.mode === 'quantity'. Both modes then
  // run the same maths against `measure`, mirroring free-shipping-component.liquid exactly — if
  // these two ever disagree the bar jumps when the optimistic mirror is replaced by the real cart.
  function freeShippingMarkup(cfg, money, subtotalCents, units) {
    if (!cfg || !cfg.thresholds || !cfg.thresholds.length) return '';
    var qtyMode = cfg.mode === 'quantity';
    var subtotal = (subtotalCents || 0) / 100;
    var measure = qtyMode ? (units || 0) : subtotal;
    var thr = cfg.thresholds.map(Number);
    var n = thr.length;
    var achieved = 0, i;
    for (i = 0; i < n; i++) { if (measure >= thr[i]) achieved++; }
    var progress;
    if (achieved >= n) {
      progress = 100;
    } else {
      var prev = achieved > 0 ? thr[achieved - 1] : 0;
      var next = thr[achieved];
      var denom = next - prev;
      var seg = denom > 0 ? Math.min(1, Math.max(0, (measure - prev) / denom)) : 0;
      progress = (achieved + seg) * 100 / n;
    }
    var found = false, remaining = 0, reward = '';
    for (i = 0; i < n; i++) {
      if (!found && measure < thr[i]) {
        found = true;
        remaining = thr[i] - measure;
        reward = (cfg.rewards && cfg.rewards[i]) || '';
      }
    }
    var amount = qtyMode
      ? '<span class="qty">' + esc(remaining + ' ' + (remaining === 1
          ? (cfg.unitOne || 'pair') : (cfg.unitMany || 'pairs'))) + '</span>'
      : '<span class="money">' + esc(formatMoney(remaining * 100, money)) + '</span>';
    var msg = found
      ? '<span>' + esc(qtyMode ? (cfg.qtyPrefix || 'Add another') : (cfg.prefix || 'Spend another')) + ' </span>' +
        amount +
        '<span> ' + esc(cfg.connector || 'for') + ' </span>' +
        '<span class="text">' + esc(reward) + '</span>'
      : '<span class="qualify-msg">' + esc(cfg.qualified || 'You qualify for all promotions!') + '</span>';
    var dots = '';
    for (i = 0; i < n; i++) {
      var pos = (i + 1) * 100 / n;
      var dotAchieved = measure >= thr[i];
      var gift = cfg.gifts && String(cfg.gifts[i]) === '1';
      dots += '<span class="promo-progress__dot' + (dotAchieved ? ' is-achieved' : '') +
        (gift ? ' promo-progress__dot--gift' : '') + '" style="left:' + pos + '%;" aria-hidden="true"></span>';
    }
    // Match the real component's classes exactly — free-shipping-component.css scopes all its rules
    // (incl. the 11px message text) under `.haloCalculatorShipping`.
    return '<free-shipping-component class="promo-progress-bar-ext haloCalculatorShipping" style="--progress-color:' +
        esc(cfg.color || '#1b1b1b') + ';">' +
        '<div class="shipping-text-message-container">' + msg + '</div>' +
        '<div class="promo-progress" style="--progress:' + progress + '%;">' +
          '<div class="promo-progress__track"><div class="promo-progress__meter"></div>' + dots + '</div>' +
        '</div>' +
      '</free-shipping-component>';
  }

  // A single cart line, reusing the real cart-items table classes so it inherits the loaded styling.
  function lineRowMarkup(line, t) {
    t = t || i18n();
    var img = line.img
      ? '<img class="cart-items__media-image border-style" src="' + line.img + '" alt="" width="120" height="120" loading="eager">'
      : '';
    var name = esc(line.colour || line.title);
    var sub = line.colour && line.title && line.title !== line.colour
      ? '<span class="sub-cstm-title">' + esc(line.title) + '</span>' : '';
    var subcat = line.subcategory
      ? '<span class="subcat-cstm-title">' + esc(line.subcategory) + '</span>' : '';
    var variant = line.variant
      ? '<dl class="cart-items__variants"><div class="cart-items__variant"><dd>' + esc(line.variant) + '</dd></div></dl>' : '';
    // Inert quantity stepper (aria-hidden, non-button, readonly) — the functional one arrives on reconcile.
    var qty =
      '<div class="quantity-selector cart-primary-typography" aria-hidden="true">' +
        '<span class="button quantity-minus button-unstyled"><span class="svg-wrapper icon-plus">' + ICON_MINUS + '</span></span>' +
        '<input type="number" value="1" min="1" tabindex="-1" readonly>' +
        '<span class="button quantity-plus button-unstyled"><span class="svg-wrapper icon-plus">' + ICON_PLUS + '</span></span>' +
      '</div>';
    // data-key makes the new card / grid / centring / baseline CSS apply so the optimistic row is
    // byte-identical to the server one and morphs seamlessly. Qty + trash live in the price column.
    return (
      '<tr class="cart-items__table-row archies-optimistic-row" role="listitem" data-key="optimistic">' +
        '<td class="cart-items__media" role="presentation">' +
          '<span class="cart-items__media-container" style="--ratio:1;">' + img + '</span>' +
        '</td>' +
        '<td class="cart-items__details cart-primary-typography" role="presentation">' +
          '<p><span class="cart-items__title-ext cart-items__title">' +
            '<span class="main-cstm-title">' + name + '</span>' + sub + subcat +
          '</span></p>' +
          variant +
          '<p class="archies-optimistic-status" aria-hidden="true">' + esc(t.adding) + '</p>' +
        '</td>' +
        '<td class="cart-items__price cart-secondary-typography" role="presentation">' +
          '<div class="cart-items__price-top">' +
            '<span class="cart-items__price-amount">' + esc(line.price) + '</span>' +
            '<span class="cart-items__remove cart-items__remove--icon" aria-hidden="true"><span class="svg-wrapper">' + ICON_DELETE + '</span></span>' +
          '</div>' +
          qty +
        '</td>' +
      '</tr>'
    );
  }

  // The full loaded-cart mirror used when adding to an EMPTY cart.
  function filledMirror(line, cfg) {
    var t = cfg.t;
    var fsBar = freeShippingMarkup(cfg.fs, cfg.money, line.priceCents * line.qty, line.qty);
    var subtotal = esc(formatMoney(line.priceCents * line.qty, cfg.money));
    // Adding to an EMPTY cart → count is always 1, so the singular "1 Item" string is correct here.
    // Build "Your Cart" + the count as plain text (capitalised via CSS), matching the server drawer.
    var titleLabel = esc(t.cartTitleLabel || 'Your Cart');
    var itemCount = esc(t.itemCount || '1 item');
    var cartTitle =
      '<span class="cart-drawer__heading-label">' + titleLabel + '</span>' +
      '<span class="cart-drawer__heading-count">' + itemCount + '</span>';
    return (
      '<div class="cart-drawer__header cart-drawer__header-ext drawer-not-empty" id="cart-drawer-header" aria-busy="true">' +
        '<div class="header-left-side"><h2 class="cart-drawer__heading h5" id="cart-drawer-title">' + cartTitle + '</h2></div>' +
        '<button type="button" class="button cart-drawer__close-button button-unstyled archies-optimistic-close" aria-label="' + esc(t.close) + '">' +
          '<span class="svg-wrapper"><svg viewBox="0 0 18 18" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M2 2l14 14M16 2L2 16"/></svg></span>' +
        '</button>' +
      '</div>' +
      fsBar +
      '<div class="cart-drawer__content motion-reduce" aria-label="' + esc(t.cart) + '" style="--header-height: 60px;">' +
        '<scroll-hint class="cart-drawer__items">' +
          '<div class="cart-items__wrapper-ext cart-items__wrapper">' +
            // The .cart-form wrapper is required: the cart drawer keys its empty-state centring off
            // `.cart-drawer:not(:has(.cart-form))`, so without it the filled layout renders centred.
            '<form class="cart-form" id="cart-form" action="/cart" method="post">' +
              '<div class="cart-items spacing-style">' +
                '<table class="cart-items__table" role="list"><tbody role="presentation">' + lineRowMarkup(line, t) + '</tbody></table>' +
              '</div>' +
            '</form>' +
          '</div>' +
        '</scroll-hint>' +
        '<div class="cart-drawer__summary inner-content">' +
          // Mirror cart-summary-ext: the v2 totals block (Subtotal 20px + Shipping) — the
          // "Estimated total" container is display:none in cart-summary-ext.css.
          '<div class="cart__summary-totals">' +
            '<div class="cart-summary-totals-v2">' +
              '<p id="subtotal-items-price" class="subtotal-items-price"> ' + esc(t.subtotal) + ': <span> ' + subtotal + ' </span></p>' +
              '<p class="shipping-items-price"> ' + esc(t.estimatedShipping) + ': <span> ' + esc(t.calculatedAtCheckout) + ' </span></p>' +
            '</div>' +
          '</div>' +
          '<div class="cart__ctas">' +
            '<button type="button" class="cart__checkout-button-ext cart__checkout-button button archies-optimistic-checkout">' +
              LOCK_SVG + ' ' + esc(t.secureCheckout) +
            '</button>' +
          '</div>' +
          '<div class="archies-optimistic-payments"></div>' +
        '</div>' +
      '</div>'
    );
  }

  function bumpBubble(bubble, by) {
    if (!bubble) return;
    var nums = (bubble.textContent || '').match(/\d+/g);
    var n = nums ? parseInt(nums[nums.length - 1], 10) : 0;
    bubble.textContent = String(n + by);
  }

  function onAddClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('button[name="add"], [type="submit"][name="add"]');
    if (!button) return;
    if (button.disabled || button.getAttribute('disabled') === 'true' || button.getAttribute('aria-disabled') === 'true') return;

    var form = button.closest('form');
    if (!form || !/\/cart\/add/.test(form.getAttribute('action') || '')) return; // product add forms only

    // a11y C2: a gift card to a recipient whose email Shopify will reject (422) must not open the
    // drawer over the form's error — let the real response decide. The browser's own email check
    // passes "a@b"; Shopify also needs a dot in the domain, so test that shape too.
    if (form.querySelector('.gc-recipient__checkbox:checked')) {
      var recipient = form.querySelector('input[name="properties[Recipient email]"]');
      var address = recipient ? recipient.value.trim() : '';
      if (recipient && (!recipient.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@.]+$/.test(address))) return;
    }

    var cart = cartItems();
    var drawer = getDrawer();
    if (!cart || !drawer) return; // no drawer → let the normal flow handle it

    var cfg = getConfig(drawer);
    var line = readLine(button, cfg.money);
    var bubble = cartBubble();
    var dialog = drawer.querySelector('dialog');

    snapshot = {
      html: cart.innerHTML,
      bubbleText: bubble ? bubble.textContent : null,
      dialogClass: dialog ? dialog.className : null
    };
    pending = true;

    var tbody = cart.querySelector('.cart-items__table tbody');
    if (tbody) {
      // Non-empty cart → prepend the new line; existing chrome (bar/summary) reconciles on cart:update.
      tbody.insertAdjacentHTML('afterbegin', lineRowMarkup(line, cfg.t));
      var heading = cart.querySelector('.cart-drawer__heading');
      if (heading) {
        heading.textContent = heading.textContent.replace(/\((\d+)\)/, function (m, d) {
          return '(' + (parseInt(d, 10) + line.qty) + ')';
        });
      }
    } else {
      // Empty cart → render the full loaded-cart mirror. Drop `cart-drawer--empty` so the empty-state
      // overrides (centred text, collapsed header) don't leak into the filled layout — the real filled
      // cart never carries that class, and morphSection restores the correct class on reconcile.
      if (dialog) dialog.classList.remove('cart-drawer--empty');
      cart.innerHTML = filledMirror(line, cfg);
      var close = cart.querySelector('.archies-optimistic-close');
      if (close) close.addEventListener('click', function () { if (drawer.close) drawer.close(); });
      // Optimistic Secure Checkout: never proceed before the server confirms the add — if still
      // pending, wait for the next cart:update, then go to checkout.
      var checkout = cart.querySelector('.archies-optimistic-checkout');
      if (checkout) checkout.addEventListener('click', function () {
        if (pending) {
          document.addEventListener('cart:update', function () { window.location.href = '/checkout'; }, { once: true });
        } else {
          window.location.href = '/checkout';
        }
      });
      // Clone the server-rendered payment icons (region-safe) into the optimistic footer.
      var tpl = drawer.querySelector('#archies-optimistic-payment-icons');
      var slot = cart.querySelector('.archies-optimistic-payments');
      if (tpl && slot && tpl.content) slot.appendChild(tpl.content.cloneNode(true));
    }

    bumpBubble(bubble, line.qty);

    if (typeof drawer.open === 'function') drawer.open();
    else if (typeof drawer.showDialog === 'function') drawer.showDialog();

    announce(cfg.t.added);
  }

  // Success: CartItemsComponent morphs the real cart in on cart:update — reconciliation is automatic.
  document.addEventListener('cart:update', function () {
    if (pending) {
      pending = false;
      snapshot = null;
    }
    // A4: announce the new cart state once the CartItemsComponent has morphed in the updated DOM.
    setTimeout(announceCartUpdate, 120);
  });

  // Failure: revert to the pre-add state and surface the error. On `window`: product-form.js
  // dispatches cart:error there, so a `document` listener never ran (a11y C2, 2026-10-13).
  window.addEventListener('cart:error', function (event) {
    if (!pending || !snapshot) return;
    var cart = cartItems();
    if (cart) cart.innerHTML = snapshot.html;
    var bubble = cartBubble();
    if (bubble && snapshot.bubbleText != null) bubble.textContent = snapshot.bubbleText;
    var drawer = getDrawer();
    var dialog = drawer && drawer.querySelector('dialog');
    if (dialog && snapshot.dialogClass != null) dialog.className = snapshot.dialogClass;
    if (drawer && typeof drawer.close === 'function') drawer.close();
    pending = false;
    snapshot = null;
    var message = (event.detail && event.detail.data && event.detail.data.message) || i18n().error;
    announce(message, true);
  });

  document.addEventListener('click', onAddClick, true); // capture, so it runs before the form submit
})();
